import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { BrowseTabType } from "../../../../types";
import { BrowseFilters } from "./BrowseFilters";
import { FilterState } from "../../utils/browseUrlParser";

const SIDEBAR_WIDTH = 246;

export type SidebarMode = "push" | "overlay" | "fullscreen";

interface BrowseFiltersSidebarProps {
  tab: BrowseTabType;
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onClose: () => void;
  onClearAll?: () => void;
  isOpen: boolean;
  topOffset?: number;
  mode?: SidebarMode;
}

export function BrowseFiltersSidebar({
  tab,
  filters,
  onFilterChange,
  onClose,
  onClearAll,
  isOpen,
  topOffset = 0,
  mode = "push",
}: BrowseFiltersSidebarProps) {
  const isFullscreen = mode === "fullscreen";

  const content = (
    <>
      <Flex align="center" justify="space-between" mb="16px">
        <Text
          fontSize="16px"
          fontWeight="600"
          fontFamily="Bricolage Grotesque"
          color="neutral.200"
          lineHeight="1.3"
        >
          Filters
        </Text>
        <Flex align="center" gap="8px">
          {onClearAll && (
            <Button
              variant="ghost"
              size="xs"
              fontFamily="Roboto"
              fontSize="12px"
              fontWeight="500"
              color="blaze.300"
              _hover={{ color: "blaze.200", bg: "transparent.white.05" }}
              px="8px"
              h="24px"
              borderRadius="4px"
              onClick={onClearAll}
            >
              Clear all
            </Button>
          )}
          <IconButton
            aria-label="Close filters"
            icon={<CloseIcon boxSize="10px" />}
            variant="ghost"
            color="neutral.400"
            size="xs"
            borderRadius="full"
            _hover={{ bg: "transparent.white.10" }}
            onClick={onClose}
          />
        </Flex>
      </Flex>
      <BrowseFilters
        isModal={true}
        tab={tab}
        filters={filters}
        onFilterChange={onFilterChange}
      />
    </>
  );

  const scrollProps = {
    overflowY: "auto" as const,
    overscrollBehavior: "contain" as const,
    sx: {
      "&::-webkit-scrollbar": { width: "4px" },
      "&::-webkit-scrollbar-track": { bg: "transparent" },
      "&::-webkit-scrollbar-thumb": { bg: "transparent.white.10", borderRadius: "2px" },
    },
  };

  if (isFullscreen) {
    return (
      <Box
        position="fixed"
        top={`${topOffset}px`}
        left={0}
        right={0}
        bottom={0}
        bg="charcoal.charcoal"
        zIndex={10}
        px="16px"
        pt="16px"
        pb="80px"
        transform={isOpen ? "translateX(0)" : "translateX(100%)"}
        transition="transform 0.2s ease-in-out"
        {...scrollProps}
      >
        {content}
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          px="16px"
          py="16px"
          bg="charcoal.charcoal"
          borderTop="1px solid"
          borderColor="transparent.white.10"
          zIndex={11}
        >
          <Button
            variant="primary"
            width="100%"
            onClick={onClose}
          >
            See Results
          </Button>
        </Box>
      </Box>
    );
  }

  if (mode === "overlay") {
    return (
      <>
        {isOpen && (
          <Box
            position="fixed"
            inset={0}
            top={`${topOffset}px`}
            bg="blackAlpha.600"
            zIndex={2}
            onClick={onClose}
          />
        )}
        <Box
          position="fixed"
          right={0}
          top={`${topOffset}px`}
          h={`calc(100vh - ${topOffset}px)`}
          w={isOpen ? `${SIDEBAR_WIDTH}px` : "0px"}
          overflow="hidden"
          transition="width 0.2s ease-in-out"
          bg="charcoal.charcoal"
          borderLeft="1px solid"
          borderColor={isOpen ? "transparent.white.10" : "transparent"}
          zIndex={3}
        >
          <Box w={`${SIDEBAR_WIDTH}px`} px="16px" py="16px" h="full" {...scrollProps}>
            {content}
          </Box>
        </Box>
      </>
    );
  }

  // Push mode: spacer reserves width in the flow, fixed box stays pinned
  return (
    <Box
      w={isOpen ? `${SIDEBAR_WIDTH}px` : "0px"}
      flexShrink={0}
      transition="width 0.2s ease-in-out"
    >
      <Box
        position="fixed"
        right={0}
        top={`${topOffset}px`}
        h={`calc(100vh - ${topOffset}px)`}
        w={isOpen ? `${SIDEBAR_WIDTH}px` : "0px"}
        overflow="hidden"
        transition="width 0.2s ease-in-out"
        borderLeft="1px solid"
        borderColor={isOpen ? "transparent.white.10" : "transparent"}
        bg="charcoal.charcoal"
      >
        <Box
          w={`${SIDEBAR_WIDTH}px`}
          h="full"
          pl="24px"
          pr="16px"
          py="16px"
          {...scrollProps}
        >
          {content}
        </Box>
      </Box>
    </Box>
  );
}
